import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import RoleBasedHeader from "../../../components/ui/RoleBasedHeader";
import WelcomeBanner from "../../../components/welcome/WelcomeBanner";
import KPICard from "../../role-based-dashboard/components/KPICard";
import ProjectCard from "../../role-based-dashboard/components/ProjectCard";
import QuickActions from "../../role-based-dashboard/components/QuickActions";
import TaskSummary from "../../role-based-dashboard/components/TaskSummary";
import TeamOverview from "../../role-based-dashboard/components/TeamOverview";
import sessionService from "../../../utils/sessionService";
import apiService from "../../../utils/apiService";
import authService from "../../../utils/authService";
import teamService from "../../../utils/teamService";
import notificationService from "../../../utils/notificationService";
import { listenForProjectUpdates } from "../../../utils/projectEventService";
import { useUserProfile } from "../../../hooks/useUserProfile";
import CreateProjectModal from "../../../components/modals/CreateProjectModal";
import { useAdminProjectCreation } from "../../../hooks/useFeatureFlags";
import useToast from "../../../hooks/useToast";
import { getProgressFromStatus } from "../../../utils/taskProgress";

const MemberDashboard = () => {
  const location = useLocation();

  // Use centralized user profile hook
  const {
    currentOrganization: hookCurrentOrganization,
    availableOrganizations: _availableOrganizations, // eslint-disable-line no-unused-vars
    loading: _profileLoading, // eslint-disable-line no-unused-vars
  } = useUserProfile();

  const { showToast } = useToast();

  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");

  // Real team members data
  const [teamMembers, setTeamMembers] = useState([]);

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);

  // Removed unused tasks-related state for optimized member dashboard

  // FIXED: Memoize organizationId
  const organizationId = React.useMemo(() => {
    return (
      sessionService.getOrganizationId() ||
      organizations?.[0]?.id ||
      hookCurrentOrganization?.id
    );
  }, [organizations, hookCurrentOrganization?.id]);

  // Use feature flags to check for project creation permission
  const { enabled: canCreateProjects } =
    useAdminProjectCreation(organizationId);

  // FIXED: Memoize the helper function to prevent infinite loops
  const getCurrentOrganizationId = useCallback(() => {
    return organizations?.[0]?.id || hookCurrentOrganization?.id;
  }, [organizations, hookCurrentOrganization]);

  // Function to reload notifications
  const reloadNotifications = useCallback(async () => {
    const organizationId = getCurrentOrganizationId();
    if (!organizationId) return;

    // Notifications logic removed
    return;
  }, [getCurrentOrganizationId]);

  // EFFECT 1: Initial user and organization load (runs once)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const currentUser = sessionService.getCurrentUser();
        if (currentUser) {
          setCurrentUser(currentUser);
          try {
            const userResult = await apiService.users.getCurrentUser();
            if (userResult?.organizations) {
              setOrganizations(userResult.organizations);
            } else {
              setOrganizations([{
                id: sessionService.getOrganizationId() || "fallback-org",
                name: "Organization",
                domain: "example.com",
              }]);
            }
          } catch (error) {
            console.error("MEMBER: Failed to load organizations:", error);
            setOrganizations([{
              id: sessionService.getOrganizationId() || "fallback-org",
              name: "Organization",
              domain: "example.com",
            }]);
          }
        }
      } catch (error) {
        console.error("Failed to load initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // EFFECT 2: Organization-specific data loading (runs when organizationId changes)
  useEffect(() => {
    if (!organizationId) return;

    const loadOrganizationData = async () => {
      try {
        setTasksLoading(true);
        const realApiService = (await import("../../../utils/realApiService.js")).default;

        // Parallel fetch for performance
        const [projectsRes, membersRes, tasksRes, statsRes] = await Promise.allSettled([
          apiService.projects.getAll(organizationId),
          teamService.getTeamMembers(organizationId),
          realApiService.taskAssignment.getMyAssignedTasks({ organization_id: organizationId }),
          realApiService.dashboardApi.getMemberDashboard(organizationId)
        ]);

        // Handle Projects
        if (projectsRes.status === 'fulfilled') {
          setProjects(projectsRes.value || []);
        }

        // Handle Team Members
        if (membersRes.status === 'fulfilled') {
          let allMembers = (membersRes.value || []).map(m => ({ ...m, status: m.status || "active" }));
          // Ensure current user is in list
          if (!allMembers.some(m => m.id === currentUser?.id || m.email === currentUser?.email)) {
            allMembers.unshift({
              id: currentUser?.id,
              name: `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() || currentUser?.email?.split("@")[0] || "You",
              email: currentUser?.email,
              role: currentUser?.role || "member",
              status: "active",
              avatar: currentUser?.avatar || "/assets/images/avatar.jpg",
              isCurrentUser: true
            });
          }
          setTeamMembers(allMembers);
        }

        // Handle Tasks
        if (tasksRes.status === 'fulfilled') {
          const list = Array.isArray(tasksRes.value?.tasks) ? tasksRes.value.tasks : (Array.isArray(tasksRes.value) ? tasksRes.value : []);
          const mapped = list.map(t => {
            const rawStatus = (t.status || "todo").toLowerCase();
            let normalizedStatus = "To Do";
            if (rawStatus.includes("progress")) normalizedStatus = "In Progress";
            else if (rawStatus === "review") normalizedStatus = "Review";
            else if (rawStatus === "blocked") normalizedStatus = "Blocked";
            else if (rawStatus === "done" || rawStatus === "completed") normalizedStatus = "Done";

            return {
              id: t.id,
              title: t.title || t.name || "Untitled",
              status: normalizedStatus,
              priority: (t.priority || "Medium").charAt(0).toUpperCase() + (t.priority || "Medium").slice(1),
              dueDate: t.due_date || t.dueDate || null,
              project: t.project?.name || t.project_name || null,
              projectId: t.project?.id || t.project_id
            };
          });
          setTasks(mapped);

          // Calculate overdue tasks client-side as fallback/validation
          const overdueCount = mapped.filter(t => {
            if (!t.dueDate || t.status === "Done") return false;
            const dueDate = new Date(t.dueDate);
            const today = new Date();
            today.setHours(0,0,0,0);
            dueDate.setHours(0,0,0,0);
            return dueDate < today;
          }).length;

          // Merge with backend stats if available
          if (statsRes.status === 'fulfilled' && statsRes.value?.stats) {
            setDashboardData({
              ...statsRes.value.stats,
              overdue_tasks: statsRes.value.stats.overdue_tasks || overdueCount
            });
          } else {
            setDashboardData(prev => ({
              ...prev,
              overdue_tasks: overdueCount
            }));
          }
        }
      } catch (error) {
        console.error("Error loading organization data:", error);
      } finally {
        setTasksLoading(false);
      }
    };

    loadOrganizationData();
  }, [organizationId, currentUser]);

  // Handle welcome message once on mount
  useEffect(() => {
    if (location.state?.message && location.state?.type === "success") {
      setWelcomeMessage(location.state.message);
      setShowWelcome(true);
      // Clear navigation state
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.message, location.state?.type, setWelcomeMessage, setShowWelcome]);

  // FIXED: Listen for real-time project updates with stable dependency
  useEffect(() => {
    const organizationId = getCurrentOrganizationId();
    if (!organizationId) return;

    const unsubscribe = listenForProjectUpdates((updateData) => {
      const { action, project, organizationId: eventOrgId } = updateData;

      // Only handle updates for our organization
      if (eventOrgId !== organizationId) return;

      if (action === "created" && project) {
        setProjects((prevProjects) => [...prevProjects, project]);
      } else if (action === "updated" && project) {
        setProjects((prevProjects) => {
          const existingIndex = prevProjects.findIndex(
            (p) => p.id === project.id,
          );
          if (existingIndex >= 0) {
            // Update existing project
            const newProjects = [...prevProjects];
            newProjects[existingIndex] = project;
            return newProjects;
          }
          return prevProjects;
        });
      } else if (action === "deleted" && project) {
        setProjects((prevProjects) =>
          prevProjects.filter((p) => p.id !== project.id),
        );
      } else if (action === "refresh") {
        // Projects will be refreshed automatically by the event system
        console.log("Project refresh requested");
      }
    });

    return unsubscribe;
  }, [getCurrentOrganizationId]); // Use the memoized function

  // Calculate real-time dashboard stats from loaded data
  useEffect(() => {
    if (projects.length > 0 || teamMembers.length > 0 || tasks.length > 0) {
      const completedTaskCount = tasks.filter(
        (t) => getProgressFromStatus(t.status) === 100,
      ).length;

      const overdueTaskCount = tasks.filter((t) => {
        if (!t.dueDate) return false;
        const s = (t.status || "").toLowerCase();
        const isDone = ["done", "completed", "complete"].includes(s);
        if (isDone) return false;
        const dueDate = new Date(t.dueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      }).length;

      const calculatedStats = {
        total_projects: projects.length,
        total_members: teamMembers.length,
        total_tasks: tasks.length,
        completed_tasks: completedTaskCount,
        overdue_tasks: overdueTaskCount,
        in_progress_tasks: tasks.filter(
          (t) => getProgressFromStatus(t.status) === 50,
        ).length,
        todo_tasks: tasks.filter((t) => getProgressFromStatus(t.status) === 0)
          .length,
      };

      // Only update if we have meaningful data
      setDashboardData((prev) => {
        // Simple equality check to avoid redundant updates
        if (
          prev &&
          prev.total_projects === calculatedStats.total_projects &&
          prev.total_tasks === calculatedStats.total_tasks &&
          prev.completed_tasks === calculatedStats.completed_tasks &&
          prev.overdue_tasks === calculatedStats.overdue_tasks
        ) {
          return prev;
        }
        return calculatedStats;
      });
    }
  }, [projects.length, teamMembers.length, tasks]); // Removed dashboardData from dependencies

  const handleCreateProject = useCallback(
    async (projectData) => {
      try {
        if (!organizationId) {
          throw new Error("No organization found");
        }

        console.log("Member creating project:", projectData);
        const result = await apiService.projects.create(
          organizationId,
          projectData,
        );

        if (result) {
          // Refresh projects list
          const projectsResult =
            await apiService.projects.getAll(organizationId);
          setProjects(projectsResult || []);

          setShowCreateProject(false);
          showToast(
            `Project "${result.name || projectData.name}" created successfully!`,
            "success",
          );
        }
      } catch (error) {
        console.error("Failed to create project:", error);
        throw error;
      }
    },
    [organizationId, showToast],
  );

  const handleOpenCreateProject = useCallback(() => {
    setShowCreateProject(true);
  }, []);

  const handleCloseCreateProject = useCallback(() => {
    setShowCreateProject(false);
  }, []);

  const getMemberKPIData = useCallback(() => {
    const realData = dashboardData || {};
    const projectCount = projects.length || realData.total_projects || 0;
    const memberCount = teamMembers.length || realData.total_members || 0;

    // Filter out completed tasks to get accurate pending count
    const pendingTaskCount = tasks.filter(
      (t) => !["Done", "Completed", "done", "completed"].includes(t.status),
    ).length;

    const completedTaskCount = tasks.filter((t) =>
      ["Done", "Completed", "done", "completed"].includes(t.status),
    ).length;

    // Use backend stats if available, otherwise fallback to local calculation
    // Backend 'in_progress_tasks' + 'todo_tasks' = pending count
    const backendPending =
      (realData.in_progress_tasks || 0) + (realData.todo_tasks || 0);
    const taskCount =
      backendPending || pendingTaskCount || realData.pending_tasks || 0;
    const completedCount = realData.completed_tasks || completedTaskCount || 0;
    const overdueCount =
      realData.my_tasks_stats?.overdue ??
      realData.overdue_tasks ??
      tasks.filter((t) => {
        if (!t.dueDate) return false;
        const s = (t.status || "").toLowerCase();
        if (["done", "completed", "complete"].includes(s)) return false;
        const dueDate = new Date(t.dueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      }).length;

    return [
      {
        title: "My Projects",
        value: projectCount.toString(),
        change: projectCount > 0 ? `+${projectCount}` : null,
        changeType: projectCount > 0 ? "positive" : "neutral",
        icon: "FolderOpen",
        color: "primary",
        changeLabel: null,
      },
      {
        title: "Team Members",
        value: memberCount.toString(),
        change: memberCount > 0 ? `+${memberCount}` : null,
        changeType: memberCount > 0 ? "positive" : "neutral",
        icon: "Users",
        color: "primary",
        changeLabel: null,
      },
      {
        title: "Pending Tasks",
        value: taskCount.toString(),
        change: null,
        changeType: "neutral",
        icon: "ClipboardList",
        color: "primary",
        changeLabel: null,
      },
      {
        title: "Overdue",
        value: overdueCount.toString(),
        change: null,
        changeType: overdueCount > 0 ? "negative" : "neutral",
        icon: "AlertCircle",
        color: "error",
        changeLabel: null,
      },
      {
        title: "Completed Tasks",
        value: completedCount.toString(),
        change: null,
        changeType: "neutral",
        icon: "CheckCircle",
        color: "success",
        changeLabel: null,
      },
    ];
  }, [dashboardData, projects, teamMembers, tasks]); // Dependencies fixed to include full arrays as expected by ESLint

  // Filter projects based on search and filter criteria - members see all organization projects
  const filteredProjects = React.useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch = project.name
        .toLowerCase()
        .includes(searchValue.toLowerCase());
      const matchesFilter =
        filterValue === "all" || project.status === filterValue;
      return matchesSearch && matchesFilter;
    });
  }, [projects, searchValue, filterValue]);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">
            Loading your member dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">
            Dashboard Error
          </h2>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors duration-200"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="aurora-container relative obsidian-scrollbar">
      {/* Global Aurora Background Blobs */}
      <div className="aurora-blob top-0 -left-64 w-[700px] h-[700px] bg-blue-500/10 dark:bg-blue-600/5 transition-all duration-1000"></div>
      <div className="aurora-blob bottom-0 -right-64 w-[700px] h-[700px] bg-indigo-500/10 dark:bg-indigo-600/5 transition-all duration-1000"></div>
      <div className="relative animate-page-in pt-24">
        {/* Enhanced Welcome Banner for New Users */}
        <div className="px-10 relative z-10">
          <WelcomeBanner />
        </div>

        {/* Legacy Welcome Message (fallback) */}
        {showWelcome && welcomeMessage && (
          <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4 mb-6 mx-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400 dark:text-green-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  {welcomeMessage}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setShowWelcome(false)}
                  className="text-green-400 hover:text-green-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <RoleBasedHeader
          userRole="member"
          currentUser={currentUser}
          currentOrganization={organizations[0]}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filterValue={filterValue}
          onFilterChange={setFilterValue}
        />

        {/* Dashboard content */}
        <main id="main-content" className="max-w-[1600px] mx-auto px-10 py-8">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            {getMemberKPIData().map((kpi, index) => (
              <KPICard key={index} {...kpi} />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            {/* Left Column - Projects */}
            <div className="lg:col-span-3 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
                    My Projects
                  </h2>
                  <p className="text-slate-600 dark:text-slate-400 mt-1">
                    View and collaborate on organization projects
                  </p>
                </div>
                {canCreateProjects && (
                  <button
                    onClick={handleOpenCreateProject}
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors duration-200 shadow-sm"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    New Project
                  </button>
                )}
              </div>

              {/* Projects Grid - Scrollable Container */}
              <div className="h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 scrollbar-track-transparent scrollbar-thumb-rounded-full hover:scrollbar-thumb-slate-300 dark:hover:scrollbar-thumb-slate-600 transition-colors">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                  {filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        userRole="member"
                      />
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-12 bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-slate-400 dark:text-slate-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                        No projects available
                      </h3>
                      <p className="text-slate-500 dark:text-slate-400 mb-6">
                        Contact your admin or owner to get access to projects.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Quick Actions and Notifications */}
            <div className="space-y-6">
              <QuickActions
                userRole="member"
                currentProject={
                  filteredProjects.length > 0 ? filteredProjects[0] : null
                }
                canCreateProjects={canCreateProjects}
                onCreateProject={handleOpenCreateProject}
              />
              {/* <NotificationPanel
              notifications={notifications}
              loading={notificationsLoading}
              onNotificationUpdate={reloadNotifications}
            /> */}
            </div>
          </div>

          {/* Bottom Section - Tasks and Team */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            <div className="h-[500px]">
              <TaskSummary
                tasks={tasks}
                userRole="member"
                loading={tasksLoading}
                counts={
                  dashboardData?.my_tasks_stats
                    ? {
                        total: dashboardData.my_tasks_stats.total,
                        completed: dashboardData.my_tasks_stats.completed,
                        inProgress: dashboardData.my_tasks_stats.in_progress,
                        overdue: dashboardData.my_tasks_stats.overdue,
                        todo: dashboardData.my_tasks_stats.todo,
                      }
                    : dashboardData
                      ? {
                          total: dashboardData.total_tasks || tasks.length,
                          completed: dashboardData.completed_tasks || 0,
                          inProgress: dashboardData.in_progress_tasks || 0,
                          todo: dashboardData.todo_tasks || 0,
                          overdue: dashboardData.overdue_tasks || 0,
                        }
                      : null
                }
              />
            </div>
            <div className="h-[500px]">
              <TeamOverview teamMembers={teamMembers} userRole="member" />
            </div>
          </div>
        </main>
      </div>{" "}
      {/* Close animate-page-in wrapper */}
      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={handleCloseCreateProject}
        onCreateProject={handleCreateProject}
        organizationId={organizationId}
        organizationName={organizations[0]?.name || "Organization"}
      />
    </div>
  );
};

export default MemberDashboard;
